import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import * as R from 'ramda';

import DeleteModal from '@atom/components/common/DeleteModal';
import EditRoleModal from '@atom/components/common/editRoleModal/EditRoleModal';
import RoleDuplicateModal from '@atom/components/common/roleDuplicateModal/RoleDuplicateModal';
import { DELETE_ROLE, GET_ROLES } from '@atom/graph/role';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { Icon, IconButton, ListTable, Menu, Progress } from '@atom/mui';
import {
  AddIcon,
  ContentCopyIcon,
  DeleteIcon,
  EditIcon,
} from '@atom/mui/icons';
import { Role } from '@atom/types/role';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { setDisplayDate } from '@atom/utilities/timeUtilities';
import { getUserFullName } from '@atom/utilities/userUtilities';

import CreateRoleModal from './CreateRoleModal';

import classes from './rolesTable.module.css';

const {
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  TablePagination,
} = ListTable;

const { MenuItem } = Menu;

type SortDirection = 'asc' | 'desc';
type SortField = 'name' | 'updatedDate';

const RolesTable = () => {
  const [selectedRole, setSelectedRole] = useState<Role>(null);
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const [{ page, limit, sortBy, sortDirection }, { Link, updateSearchParams }] =
    useNavigation({
      page: SearchParamType.NUMBER,
      limit: SearchParamType.NUMBER,
      sortBy: SearchParamType.STRING,
      sortDirection: SearchParamType.STRING,
    });

  useEffect(() => {
    // Set initial values for sortBy, and sortDirection
    updateSearchParams({
      sortBy: sortBy || 'name',
      sortDirection: sortDirection || 'asc',
    });
  }, []);

  const [deleteRole, { loading: loadingDelete }] = useMutation(DELETE_ROLE);

  const { data, loading, refetch } = useQuery(GET_ROLES, {
    variables: {
      input: {
        page,
        limit,
        showAdmin: hasRolePermissions(ROLE_SETS.ORG_ADMIN),
        sortBy: sortBy as SortField,
        sortDirection: sortDirection as SortDirection,
      },
    },
    fetchPolicy: 'network-only',
  });

  const getSortDirection = useCallback(
    (field: SortField) => {
      if (sortBy !== field) {
        return false;
      }

      return sortDirection as SortDirection;
    },
    [sortBy, sortDirection],
  );

  const handleSortDirectionChange =
    (newSortBy: SortField) => (newSortDirection: SortDirection) => {
      updateSearchParams({
        sortBy: newSortBy,
        sortDirection: newSortDirection,
      });
    };

  const handleDelete = async () => {
    await deleteRole({
      variables: { id: selectedRole.id },
    });

    setDeleteModalOpen(false);
    refetch();
  };

  const totalCount = R.pathOr<number>(0, ['roles', 'totalCount'], data);
  const roles = R.pathOr<Role[]>([], ['roles', 'roles'], data);

  const handlePageChange = newPage => {
    updateSearchParams({ page: newPage });
  };
  const handleLimitChange = newLimit => {
    updateSearchParams({ limit: newLimit });
  };

  return (
    <>
      <div className={classes.rolesHeader}>
        <div>Roles</div>
        {hasRolePermissions(ROLE_SETS.ORG_ADMIN) && (
          <IconButton
            onClick={() => setCreateModalOpen(true)}
            data-cy="teamPortalRolesAddRoleButton"
          >
            <Icon icon={AddIcon} />
          </IconButton>
        )}
      </div>
      {loading ? (
        <div className={classes.loading}>
          <Progress />
        </div>
      ) : (
        <ListTable offsetTop="215px">
          <TableHead>
            <TableRow header>
              <TableCell
                width="11.25rem"
                variant="head"
                sortDirection={getSortDirection('name')}
                onSortChange={handleSortDirectionChange('name')}
              >
                <div>Name</div>
              </TableCell>
              <TableCell variant="head">Description</TableCell>
              <TableCell width="12.5rem" variant="head">
                Last Updated By
              </TableCell>
              <TableCell
                width="9.375rem"
                variant="head"
                sortDirection={getSortDirection('updatedDate')}
                onSortChange={handleSortDirectionChange('updatedDate')}
              >
                Last Updated On
              </TableCell>
              <TableCell variant="head" />
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map(role => (
              <TableRow key={role.id}>
                <TableCell style={{ fontWeight: 500 }}>
                  <Link to={`/team/role/${role.id}`}>{role.name}</Link>
                </TableCell>
                <TableCell>{role.description}</TableCell>
                <TableCell>{getUserFullName(role.updatedBy)}</TableCell>
                <TableCell>{setDisplayDate(role.updatedDate)}</TableCell>
                <TableCell width="48px">
                  {hasRolePermissions(ROLE_SETS.ORG_ADMIN) ? (
                    <Menu dataCyLabel={`rolesTableRoleMenu-${role.name}`}>
                      {!role.isDefault && (
                        <MenuItem
                          startAdornment={<Icon icon={EditIcon} />}
                          onClick={() => {
                            setSelectedRole(role);
                            setEditModalOpen(true);
                          }}
                          data-cy={`rolesTableEditButton-${role.id}`}
                        >
                          Edit
                        </MenuItem>
                      )}
                      <MenuItem
                        startAdornment={<Icon icon={ContentCopyIcon} />}
                        onClick={() => {
                          setSelectedRole(role);
                          setDuplicateModalOpen(true);
                        }}
                        data-cy={`rolesTableDuplicateButton-${role.id}`}
                      >
                        Duplicate
                      </MenuItem>
                      {!role.isDefault && (
                        <MenuItem
                          startAdornment={<Icon icon={DeleteIcon} />}
                          onClick={() => {
                            setSelectedRole(role);
                            setDeleteModalOpen(true);
                          }}
                          data-cy={`rolesTableDeleteButton-${role.id}`}
                        >
                          Delete
                        </MenuItem>
                      )}
                    </Menu>
                  ) : (
                    <div style={{ height: '48px' }} />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                count={totalCount}
                rowsPerPage={limit}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={event =>
                  handleLimitChange(+event.target.value)
                }
              />
            </TableRow>
          </TableFooter>
        </ListTable>
      )}
      <CreateRoleModal
        open={createModalOpen}
        onClose={shouldRefetch => {
          setCreateModalOpen(false);
          if (shouldRefetch) {
            refetch();
          }
        }}
      />
      <EditRoleModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        role={selectedRole}
      />
      <RoleDuplicateModal
        open={duplicateModalOpen}
        onClose={created => {
          setDuplicateModalOpen(false);

          if (created) {
            refetch();
          }
        }}
        role={selectedRole}
      />
      <DeleteModal
        title="Delete Role?"
        content="Deleting a role will delete all of its policies and remove it from all assigned users and user groups. Are you sure?"
        open={deleteModalOpen}
        onCancel={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        loading={loadingDelete}
      />
    </>
  );
};

export default RolesTable;
